import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

export default function EmailConfirmed() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const handleEmailConfirmation = async () => {
      try {
        // Récupérer les paramètres de l'URL (hash fragment)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const type = hashParams.get('type');
        
        // Vérifier s'il y a une erreur dans le hash (Supabase redirige avec erreur)
        const hashError = hashParams.get('error');
        const hashErrorCode = hashParams.get('error_code');
        const hashErrorDescription = hashParams.get('error_description');
        
        // Vérifier aussi les query params (certaines configs Supabase utilisent ça)
        const queryParams = new URLSearchParams(window.location.search);
        const tokenHash = queryParams.get('token_hash');
        const typeParam = queryParams.get('type');
        
        console.log('[EmailConfirmed] URL params:', { 
          accessToken: !!accessToken, 
          refreshToken: !!refreshToken, 
          type, 
          tokenHash: !!tokenHash,
          typeParam,
          hashError,
          hashErrorCode,
          hashErrorDescription
        });

        // Si Supabase a renvoyé une erreur dans le hash
        if (hashError) {
          console.error('[EmailConfirmed] Supabase returned error:', hashError, hashErrorDescription);
          let decodedError = hashErrorDescription ? decodeURIComponent(hashErrorDescription.replace(/\+/g, ' ')) : 'Erreur lors de la confirmation';
          
          // Messages d'erreur spécifiques pour les problèmes courants
          if (decodedError.includes('Database error granting user')) {
            decodedError = 'Erreur de base de données lors de la création du profil. Veuillez réessayer ou contacter le support.';
          } else if (decodedError.includes('unexpected_failure')) {
            decodedError = 'Erreur inattendue lors de la confirmation. Le lien pourrait être expiré ou déjà utilisé.';
          } else if (decodedError.includes('signup_disabled')) {
            decodedError = 'Les inscriptions sont temporairement désactivées.';
          }
          
          setErrorMessage(decodedError);
          setStatus('error');
          return;
        }

        // Si on a un token_hash (format récent de Supabase)
        if (tokenHash && typeParam === 'signup') {
          const { data, error } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: 'signup'
          });
          
          if (error) {
            console.error('[EmailConfirmed] OTP verification error:', error);
            setErrorMessage(error.message);
            setStatus('error');
            return;
          }
          
          console.log('[EmailConfirmed] Email confirmed via OTP');
          // Mettre à jour le profil
          if (data.user) {
            await supabase.from('profiles').update({ 
              account_status: 'active',
              email_confirmed_at: new Date().toISOString()
            }).eq('id', data.user.id);
          }
          setStatus('success');
        }
        // Si on a access_token et refresh_token (format token pair)
        else if (accessToken && refreshToken) {
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });
          
          if (error) {
            console.error('[EmailConfirmed] Session set error:', error);
            setErrorMessage(error.message);
            setStatus('error');
            return;
          }
          
          console.log('[EmailConfirmed] Session set successfully');
          // Mettre à jour le profil
          if (data.user) {
            await supabase.from('profiles').update({ 
              account_status: 'active',
              email_confirmed_at: new Date().toISOString()
            }).eq('id', data.user.id);
          }
          setStatus('success');
        }
        // Si on a juste access_token (ancien format)
        else if (accessToken) {
          // Essayer de récupérer la session actuelle
          const { data: { session }, error } = await supabase.auth.getSession();
          
          if (error) {
            console.error('[EmailConfirmed] Get session error:', error);
            setErrorMessage(error.message);
            setStatus('error');
            return;
          }
          
          if (session) {
            console.log('[EmailConfirmed] Session exists, email confirmed');
            setStatus('success');
          } else {
            // Pas de session, essayer exchangeCodeForSession avec le code
            const code = queryParams.get('code');
            if (code) {
              const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
              if (exchangeError) {
                console.error('[EmailConfirmed] Code exchange error:', exchangeError);
                setErrorMessage(exchangeError.message);
                setStatus('error');
                return;
              }
              setStatus('success');
            } else {
              setStatus('success'); // Assume la page a été atteinte correctement
            }
          }
        }
        // Pas de token - vérifier si déjà connecté
        else {
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            console.log('[EmailConfirmed] Already logged in');
            setStatus('success');
          } else {
            // Vérifier s'il y a un code dans les query params
            const code = queryParams.get('code');
            if (code) {
              const { error } = await supabase.auth.exchangeCodeForSession(code);
              if (error) {
                console.error('[EmailConfirmed] Code exchange error:', error);
                setErrorMessage(error.message);
                setStatus('error');
                return;
              }
              setStatus('success');
            } else {
              // Pas de token ni de session - afficher quand même success
              // car l'utilisateur peut avoir cliqué sur le lien après s'être connecté
              console.log('[EmailConfirmed] No token found, showing success anyway');
              setStatus('success');
            }
          }
        }
      } catch (err) {
        console.error('[EmailConfirmed] Unexpected error:', err);
        setErrorMessage('Une erreur inattendue s\'est produite');
        setStatus('error');
      }
    };

    handleEmailConfirmation();
  }, []);

  // Redirection automatique après succès
  useEffect(() => {
    if (status === 'success') {
      const timer = setTimeout(() => {
        navigate('/dashboard');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [status, navigate]);

  // État de chargement
  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-secondary/10 flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center space-y-6">
          <div className="flex items-center justify-center space-x-2 mb-8">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-primary">
              <Package className="h-7 w-7 text-primary-foreground" />
            </div>
            <h1 className="text-3xl font-bold bg-gradient-primary bg-clip-text text-transparent">
              Nazandoo
            </h1>
          </div>
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">Vérification de votre email...</p>
        </div>
      </div>
    );
  }

  // État d'erreur
  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-secondary/10 flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center space-y-6">
          <div className="flex items-center justify-center space-x-2 mb-8">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-primary">
              <Package className="h-7 w-7 text-primary-foreground" />
            </div>
            <h1 className="text-3xl font-bold bg-gradient-primary bg-clip-text text-transparent">
              Nazandoo
            </h1>
          </div>
          <div className="flex justify-center">
            <div className="relative">
              <div className="absolute inset-0 bg-red-500/20 rounded-full blur-xl"></div>
              <div className="relative bg-white dark:bg-gray-800 rounded-full p-6 shadow-xl">
                <AlertCircle className="h-16 w-16 text-red-500" />
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <h2 className="text-2xl font-bold">Erreur de confirmation</h2>
            <p className="text-muted-foreground">{errorMessage || 'Le lien de confirmation a expiré ou est invalide.'}</p>
            <p className="text-sm text-muted-foreground">
              Veuillez réessayer de vous inscrire ou contacter le support.
            </p>
          </div>
          <button
            onClick={() => navigate('/login')}
            className="mt-4 px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            Retour à la connexion
          </button>
        </div>
      </div>
    );
  }

  // État de succès
  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-secondary/10 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center space-y-6">
          {/* Logo */}
          <div className="flex items-center justify-center space-x-2 mb-8">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-primary">
              <Package className="h-7 w-7 text-primary-foreground" />
            </div>
            <h1 className="text-3xl font-bold bg-gradient-primary bg-clip-text text-transparent">
              Nazandoo
            </h1>
          </div>

          {/* Success Icon */}
          <div className="flex justify-center">
            <div className="relative">
              <div className="absolute inset-0 bg-green-500/20 rounded-full blur-xl"></div>
              <div className="relative bg-white dark:bg-gray-800 rounded-full p-6 shadow-xl">
                <CheckCircle className="h-16 w-16 text-green-500" />
              </div>
            </div>
          </div>

          {/* Message */}
          <div className="space-y-3">
            <h2 className="text-2xl font-bold">Email confirmé ! 🎉</h2>
            <p className="text-muted-foreground">
              Votre adresse email a été vérifiée avec succès.
            </p>
            <p className="text-sm text-muted-foreground">
              Vous allez être redirigé vers votre tableau de bord...
            </p>
          </div>

          {/* Loading indicator */}
          <div className="flex justify-center pt-4">
            <div className="flex space-x-2">
              <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
